import { useEffect, useState } from "react";
import { getHotCollections, type HotCollection } from "../api/atomicApi";
import { classifyCollection, type CollectionCategory } from "../utils/collectionCategory";
import { IpfsImage } from "./IpfsImage";

interface HotCollectionsProps {
  /** Which collection (if any) is currently filtering the listings below —
   * used to highlight the matching card. */
  activeCollection: string | null;
  onSelect: (collectionName: string) => void;
  onClear: () => void;
  /** Games vs Non-Games tab currently active on the Marketplace page —
   * this strip is filtered to match, so it doesn't show a card that
   * clicking through to would land in the other tab's listings. */
  category: CollectionCategory;
}

// Requesting more than the on-screen count (12) up front, since some of
// the raw top-N will get filtered out by category below — without this a
// niche tab (Non-Games, on a chain whose top volume is nearly all gaming
// collections) could come back with only 1-2 cards instead of a full strip.
const RAW_FETCH_LIMIT = 40;
const DISPLAY_LIMIT = 12;

function formatVolume(volumeBySymbol: Record<string, number>): string {
  const entries = Object.entries(volumeBySymbol);
  if (entries.length === 0) return "";
  return entries.map(([sym, amt]) => `${amt.toFixed(amt < 100 ? 2 : 0)} ${sym}`).join(" + ");
}

/**
 * AtomicHub-style "Hottest Collections (24h)" strip — see getHotCollections
 * in atomicApi.ts for how the ranking is computed (by completed-sale count
 * in the last 24h, chain-wide, not just this site's own traffic). Clicking
 * a card filters the Marketplace listings below to that collection, so
 * "what's hot" flows straight into "browse and buy it here" rather than
 * just being a stat.
 */
export function HotCollections({ activeCollection, onSelect, onClear, category }: HotCollectionsProps) {
  const [collections, setCollections] = useState<HotCollection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getHotCollections({ hours: 24, limit: RAW_FETCH_LIMIT })
      .then((data) => {
        if (!cancelled) {
          const matching = data
            .filter((c) => classifyCollection(c.collectionName, c.displayName) === category)
            .slice(0, DISPLAY_LIMIT);
          setCollections(matching);
        }
      })
      .catch(() => {
        /* Non-critical — just don't show the strip if this fails. */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [category]);

  if (loading || collections.length === 0) return null;

  return (
    <div className="hot-collections">
      <div className="hot-collections__header">
        <h2>🔥 Hottest Collections — 24h</h2>
        {activeCollection && (
          <button className="btn btn--small btn--outline" onClick={onClear}>
            Clear filter ✕
          </button>
        )}
      </div>
      <div className="hot-collections__scroll">
        {collections.map((c) => (
          <button
            key={c.collectionName}
            className={
              "hot-collection-card" +
              (activeCollection === c.collectionName ? " hot-collection-card--active" : "")
            }
            onClick={() => onSelect(c.collectionName)}
            title={`Filter listings to ${c.displayName}`}
          >
            <IpfsImage
              hash={c.image}
              alt={c.displayName}
              className="hot-collection-card__img"
              placeholderClassName="hot-collection-card__img-placeholder"
              loading="lazy"
            />
            <div className="hot-collection-card__name">{c.displayName}</div>
            <div className="hot-collection-card__stats">
              {c.saleCount} sale{c.saleCount === 1 ? "" : "s"}
              {formatVolume(c.volumeBySymbol) && ` · ${formatVolume(c.volumeBySymbol)}`}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
